// Chakra imports
import {
  Box,
  Flex,
  Icon,
  Progress,
  Text,
  useColorModeValue,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  Input,
  ModalFooter,
  Spinner,
} from "@chakra-ui/react";
// Custom components
import Card from "components/card/Card.js";
import IconBox from "components/icons/IconBox";
import Menu from "components/menu/MainMenu";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useEndpoints from "providers/Endpoints";
import constants from "providers/Constants";
// Assets
import { MdOutlineCloudDone, MdOutlineKey } from "react-icons/md";

export default function Banner(props) {
  const { used, total } = props;
  const { t } = useTranslation();
  // Chakra Color Mode
  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const brandColor = useColorModeValue("brand.500", "white");
  const textColorSecondary = "gray.400";
  const box = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [error, setError] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [postData, setPostData] = React.useState({});
  const [wallets, setWallets] = React.useState([]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPostData({});
    setError([]);
  };

  const endpoints = useEndpoints();

  useEffect(() => {
    getFiatWallets();
  }, []);

  return (
    <>
      <Card mb={{ base: "0px", lg: "0px" }} align='center'>
        <IconBox
          mx='auto'
          h='100px'
          w='100px'
          icon={
            <Icon as={MdOutlineKey} color={brandColor} h='46px' w='46px' />
          }
          bg={box}
        />
        <Text color={textColorPrimary} fontWeight='bold' fontSize='2xl' mt='10px'>
          {t("API Tokens")}
        </Text>
        <Text
          color={textColorSecondary}
          fontSize='md'
          maxW={{ base: "100%", xl: "80%", "3xl": "60%" }}
          mx='auto'>
          {t(
            "Accessing some API endpoints requires access tokens, Always make sure to have enough tokens to avoid any interruptions."
          )}
        </Text>
        <Box w='100%' mt='auto'>
          <Flex justify='center' mb='10px' mt='20px'>
            <Box
              as='button'
              bg={box}
              color={brandColor}
              px='4'
              py='2'
              borderRadius='30px'
              onClick={handleOpenModal}
            >
              {t('Buy Tokens')}
            </Box>
          </Flex>
          <Flex w='100%' justify='space-between' mb='10px'>
            <Text color={textColorSecondary} fontSize='sm' maxW='40%'>
              {used}
            </Text>
            <Text color={textColorSecondary} fontSize='sm' maxW='40%'>
              {total}
            </Text>
          </Flex>
          <Progress
            align='start'
            colorScheme='brandScheme'
            value={total ? (used / total) * 100 : 0}
            w='100%'
          />
        </Box>
      </Card>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Buy Tokens')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loading ? (
              <Flex justify="center" align="center" height="100%">
                <Spinner size="xl" />
              </Flex>
            ) : (
              <FormControl>
                <select
                  required
                  onChange={(e) =>
                    setPostData((prev) => ({
                      ...prev,
                      debit_currency: e.target.value,
                      token: null,
                      rate: null,
                      amount: null,
                      currency: null,
                      min: null,
                      max: null,
                    }))
                  }
                  value={postData?.debit_currency || ''}
                  defaultValue=""
                  style={{
                    width: '100%',
                    padding: '10px',
                    marginBottom: '24px',
                    borderRadius: '15px',
                    borderColor: '#E2E8F0',
                    borderWidth: '1px',
                    fontSize: 'sm',
                    fontWeight: '500',
                  }}
                >
                  <option value="" disabled>{t('Choose Debit Fiat Wallet')}</option>
                  {wallets && wallets.map((item, i) => (
                    <option key={i} value={item.country_currency_short}>
                      {item.country_currency_short} - {item.country_currency_symbol}{parseFloat(item.available_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </option>
                  ))}
                </select>
                {postData?.debit_currency ? (
                  <Input
                    isRequired={true}
                    variant='auth'
                    fontSize='sm'
                    value={postData?.token}
                    type={'number'}
                    step='0.01'
                    ms={{ base: "0px", md: "0px" }}
                    onChange={(e) => {
                      setPostData((prev) => ({
                        ...prev,
                        token: e.target.value,
                        rate: null,
                        amount: null,
                        currency: null,
                        min: null,
                        max: null,
                      }));
                    }}
                    placeholder={t('Number of tokens e.g. 1209')}
                    mb='10px'
                    fontWeight='500'
                    size='lg'
                  />
                ) : null}
                {postData?.amount && postData?.currency && postData?.rate && postData?.min && postData?.max && (
                  <Text fontSize='xs' color={'green'} ml='5px'>
                    {t('You will pay {{amount}} {{currency}} for {{token}} tokens at {{rate}} {{currency}} per token', {
                      amount: postData?.amount ? parseFloat(postData.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00',
                      currency: postData?.currency || 'N/A',
                      token: postData?.token ? parseFloat(postData.token).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00',
                      rate: postData?.rate ? parseFloat(postData.rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00',
                    })}
                  </Text>
                )}

                {
                  error.length > 0 && (
                    error.map((err, index) => (
                      <Text
                        key={index}
                        mt='10px'
                        ms='4px'
                        color={'red'}
                        fontWeight='400'
                        fontSize='sm'>
                        * {err.msg}
                      </Text>
                    ))
                  )
                }
              </FormControl>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              me='100%'
              mb='10px'
              w='140px'
              mr={3} onClick={handleCloseModal}
              minW='140px'
              mt={{ base: "20px", "2xl": "auto" }}
              variant='ghost'
              fontWeight='500'>
              {t('Close')}
            </Button>
            {
              postData?.amount && postData?.currency && postData?.rate && postData?.min && postData?.max ? (
                <Button
                  isLoading={loading}
                  isDisabled={loading}
                  me='100%'
                  mb='10px'
                  w='140px'
                  mr={1} onClick={() => {
                    proceed();
                  }}
                  fontSize={'sm'}
                  minW='200px'
                  mt={{ base: "20px", "2xl": "auto" }}
                  variant='brand'
                  fontWeight='500'>
                  {t('Pay {{amount}} {{currency}}', {
                    amount: postData?.amount ? parseFloat(postData.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00',
                    currency: postData?.currency || 'N/A',
                  })}
                </Button>
              ) : (
                <Button
                  isLoading={loading}
                  isDisabled={loading}
                  me='100%'
                  mb='10px'
                  w='140px'
                  fontSize={'sm'}
                  mr={1} onClick={() => {
                    getTokenToAmountConversion(postData?.debit_currency, postData?.token)
                  }}
                  minW='140px'
                  mt={{ base: "20px", "2xl": "auto" }}
                  variant='brand'
                  fontWeight='500'>
                  {t('Verify')}
                </Button>
              )
            }
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );

  // Get fiat wallets
  async function getFiatWallets() {
    setLoading(true);
    endpoints.getFiatWallets((error, data) => {
      if (error) {
        constants.showToast(
          "error",
          "FAILED",
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setWallets(data || []);
      }
      setLoading(false);
    });
  }

  // Get token to amount conversion
  async function getTokenToAmountConversion(debit_currency, token) {
    if (!debit_currency || !token) {
      constants.showToast("error", "FAILED", t("Snap! All fields are required"));
      return;
    }
    setLoading(true);
    endpoints.getTokenToAmountConversion(debit_currency, token, (error, data) => {
      setError([]);
      setLoading(false);
      if (error) {
        setError(error.errors || []);
        constants.showToast(
          "error",
          "FAILED",
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        setPostData((prev) => ({
          ...prev,
          amount: data?.amount,
          currency: data?.currency,
          rate: data?.rate,
          min: data?.min,
          max: data?.max,
        }));
      }
    });
  }

  // Proceed
  async function proceed() {
    if (!postData?.debit_currency || !postData?.token || !postData?.currency) {
      constants.showToast("error", "FAILED", t("Snap! All fields are required"));
      return;
    }
    if (postData?.token < postData?.min) {
      constants.showToast(
        "error",
        "FAILED",
        t('Snap! Minimum tokens you can buy is {{min}}', { min: postData?.min })
      );
      return;
    }
    setLoading(true);
    endpoints.buyTokens(postData?.currency, postData?.token, (error, data) => {
      setError([]);
      setLoading(false);
      if (error) {
        setError(error.errors || []);
        constants.showToast(
          "error",
          "FAILED",
          error?.message || t('Snap! Something went wrong, Try again')
        );
      } else {
        constants.showToast(
          "success",
          "SUCCESS",
          data?.message || t('Transaction successful')
        );
        handleCloseModal();
        window.location.reload();
      }
    });
  }
}
